import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload } from "@fortawesome/free-solid-svg-icons";
import { FileAttachment } from "./types";
import Skeleton from "react-loading-skeleton";
import defaultimage from "assets/img/img-default/Empty state.jpg";
import { useMutation, useQuery } from "@apollo/client";
import { UPLOAD_FILES } from "api/graphql/mutations/Uploadimage";
import { useParams } from "react-router-dom";

interface VariantImageProps {
  image: FileAttachment;
  onImageUpload: (newImage: FileAttachment) => void;
}

const VariantImage: React.FC<VariantImageProps> = ({ image, onImageUpload }) => {
  const { productId } = useParams<{ productId: string }>();
  const [isHovered, setIsHovered] = useState(false);
  const [localImage, setLocalImage] = useState<FileAttachment | null>(image || null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Use GraphQL mutation for image upload
  const [uploadFilesMutation] = useMutation(UPLOAD_FILES);
  const baseUrl = process.env.REACT_APP_MEDIA_SERVER_API_URL;
 
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = async () => {
      async function fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            // e.g. data:image/png;base64,iVBORw0KGgoAAAANSUh...
            const result = reader.result as string;
            const base64String = result.split(",")[1];
            resolve(base64String);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }
      const base64Str = await fileToBase64(file); // Convert file

      if (!base64Str) {
        console.error("Error encoding image.");
        setIsUploading(false);
        return;
      }

      try {
        const { data } = await uploadFilesMutation({
          variables: {
            input: [
              {
                name: file.name,
                base64_encoded_file: base64Str,
              },
            ],
          },
        });

        if (data?.UploadFiles?.items?.length > 0) {
          // Get the full path of the uploaded image
          const uploadedImageUrl = data.UploadFiles.items[0].full_path;
        
          // Modify the path only for UI display
          let uiImageUrl = uploadedImageUrl.replace(/^\/?var\/www\/html\/pub\/media\//, '');
          uiImageUrl = `${baseUrl}${uiImageUrl.startsWith('/') ? '' : '/'}${uiImageUrl}`;
        
          const newImage: FileAttachment = {
            name: file.name,
            size: Math.round(file.size / 1024), // Now correctly typed as number
            resolution: await calculateImageResolution(file),
            preview: uiImageUrl,  // Keep original URL for API
            originalUrl: uploadedImageUrl, // Use modified URL for UI
            uploadProgress: 100,
            isUploading: false,
          };

          setLocalImage(newImage);
          onImageUpload(newImage);
        } else {
          console.error("Error: No image URL returned from server.");
        }
      } catch (error) {
        console.error("Upload error:", error);
      } finally {
        setIsUploading(false);
      }
    };
  };

  const calculateImageResolution = (file: File): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      try {
        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = () => {
          resolve(`${img.width}x${img.height} px`);
        };
        img.onerror = () => {
          reject("Failed to load image");
        };
      } catch (error) {
        reject("Error processing image resolution");
      }
    });
  };

  const handleOverlayClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      className="position-relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ width: "128px", height: "128px" }}
    >
      {isUploading ? (
        <>
          <Skeleton
            width="100%"
            height="100%"
            baseColor="var(--phoenix-secondary-bg)"
            highlightColor="var(--phoenix-emphasis-bg)"
            style={{ borderRadius: "4px" }}
          />
          <div className="position-absolute bottom-0 start-0 w-100" style={{ height: "4px" }}>
            <div className="bg-primary" style={{ width: `${uploadProgress}%`, height: "100%" }} />
          </div>
        </>
      ) : (
        <img
          src={localImage?.preview || defaultimage}
          alt="Variant"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "4px",
          }}
        />
      )}
      {isHovered && !isUploading && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            color: "#fff",
            borderRadius: "4px",
            cursor: "pointer",
          }}
          onClick={handleOverlayClick}
        >
          <FontAwesomeIcon icon={faUpload} size="2x" />
          <span>Upload Custom Image</span>
        </div>
      )}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </div>
  );
};

export default VariantImage;
